import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Sun, Sunrise, Sunset, Waves, Wind } from "lucide-react";
import SectionSponsor from "./SectionSponsor";
import Lighthouse from "./Lighthouse";
import { getCoastalConditions, getHeaderPhotoToday, LOGO_URL } from "../lib/api";

const VARIANTS = {
  photo: {
    name: "Photo of the Day", wrap: "bg-[#0B2433] text-white", meta: "text-white/80", accent: "text-[#F4B860]",
    line: "text-white", chip: "bg-black/30 text-white border border-white/15", waveOp: [0.14, 0.09, 0.06],
  },
  tidewater: {
    name: "Tidewater", wrap: "bg-gradient-to-br from-[#005F73] to-[#0A9396] text-white",
    meta: "text-white/70", accent: "text-[var(--p-amber)]", line: "text-white", chip: "bg-white/15 text-white",
    lh: "text-white/25", waveOp: [0.18, 0.12, 0.08],
  },
  seaglass: {
    name: "Sea Glass", wrap: "bg-gradient-to-b from-[#EEF6F4] to-[#F8F4EC] text-[var(--p-ink)] border border-[#DCE9E6]",
    meta: "text-slate-500", accent: "text-[var(--p-teal)]", line: "text-[#0A9396]", chip: "bg-white/80 text-slate-700 border border-[#DCE9E6]",
    lh: "text-[#0A9396]/35", waveOp: [0.35, 0.22, 0.14],
  },
  dusk: {
    name: "Dusk", wrap: "bg-[#0B2433] text-white",
    meta: "text-white/55", accent: "text-[#F4B860]", line: "text-[#94D2BD]", chip: "bg-white/10 text-white/90",
    lh: "text-[#94D2BD]/35", waveOp: [0.22, 0.14, 0.09], beam: true,
  },
};

const etNow = () => new Date().toLocaleString("sv-SE", { timeZone: "America/New_York" }).slice(0, 16);
const fmtClock = (s) => {
  if (!s) return "—";
  const [h, m] = s.slice(11, 16).split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const nextTide = (tides, type) => {
  const list = (tides || []).filter((t) => t.type === type);
  const now = etNow().replace("T", " ");
  return list.find((t) => t.time >= now) || list[list.length - 1];
};

export default function CoastalMasthead() {
  const [variant, setVariant] = useState(() => localStorage.getItem("nl-header-v2") || "photo");
  const [cond, setCond] = useState(null);
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    getCoastalConditions().then(setCond).catch(() => {});
    getHeaderPhotoToday().then(setPhoto).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem("nl-header-v2", variant); }, [variant]);
  const v = VARIANTS[variant] || VARIANTS.photo;
  const isPhoto = variant === "photo" && photo;
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="mb-6">
      <section data-testid="coastal-masthead" data-variant={variant} className={`p-fade relative overflow-hidden rounded-2xl px-6 pb-14 pt-8 sm:px-10 sm:pt-10 ${isPhoto ? "sm:min-h-[340px]" : ""} ${v.wrap}`}>
        {isPhoto && <PhotoBackdrop photo={photo} />}
        {v.beam && <div aria-hidden className="coastal-beam pointer-events-none absolute right-[calc(22%+58px)] top-[27%] hidden h-[2px] w-[420px] origin-right sm:block" />}
        {v.lh && <Lighthouse className={`pointer-events-none absolute bottom-6 right-3 h-[38%] opacity-70 sm:right-[22%] sm:h-[82%] sm:max-h-[240px] sm:opacity-100 ${v.lh}`} />}
        <WaveLines color={v.line} ops={v.waveOp} />
        <div className="relative">
          <div className="flex flex-col-reverse items-start justify-between gap-3 sm:flex-row sm:gap-4">
            <p className={`portal-chip ${v.meta}`}>{today} · Amelia Island & Nassau County, FL</p>
            <SectionSponsor section="home" variant="portal" className="self-end sm:self-auto" />
          </div>
          <h1 data-testid="portal-header-logo" className={`font-portal mt-2 flex items-center gap-3 text-4xl font-black tracking-tight sm:gap-4 sm:text-5xl lg:text-6xl ${isPhoto ? "drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]" : ""}`}>
            <img src={variant === "seaglass" ? "/brand/nassau-life-mark.png" : "/brand/nassau-life-mark-light.png"} alt="" className="h-11 w-auto sm:h-14 lg:h-16" />
            <span>Nassau <span className={variant === "seaglass" ? "text-[var(--p-teal)]" : ""}>Life</span></span>
          </h1>
          <div data-testid="powered-by-news-leader" className="mt-3 inline-flex items-center gap-2.5 rounded-lg bg-white/95 px-3 py-1.5 shadow-sm">
            <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">Powered by</span>
            <img src={LOGO_URL} alt="News-Leader" className="h-6 w-auto object-contain sm:h-7" />
          </div>
          <Conditions cond={cond} chip={v.chip} />
        </div>
        {isPhoto && <PhotoCredit photo={photo} />}
      </section>
      <HeaderSwitcher variant={variant} setVariant={setVariant} />
    </div>
  );
}

function PhotoBackdrop({ photo }) {
  return (
    <>
      <img src={photo.image_url} alt={photo.caption || "Nassau County photo of the day"} data-testid="header-photo-image" className="absolute inset-0 h-full w-full object-cover" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0B2433]/80 via-[#0B2433]/45 to-transparent" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B2433]/70 via-transparent to-[#0B2433]/25" />
    </>
  );
}

function PhotoCredit({ photo }) {
  return (
    <div className="relative -mb-8 mt-6 flex flex-wrap items-center justify-between gap-2 text-[10px] text-white/75 sm:absolute sm:bottom-3 sm:left-10 sm:right-10 sm:mb-0 sm:mt-0">
      <Link to="/photo-credits" data-testid="header-photo-credit-link" className="hover:text-white hover:underline">
        Photo: {photo.photographer}{photo.location ? ` · ${photo.location}` : ""}
      </Link>
      <Link to="/photo-credits#submit" data-testid="header-submit-photo-link" className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 font-semibold text-white backdrop-blur hover:bg-white/25">
        <Camera size={11} /> Submit your photo
      </Link>
    </div>
  );
}

function Conditions({ cond, chip }) {
  const hi = nextTide(cond?.tides, "High");
  const lo = nextTide(cond?.tides, "Low");
  const items = cond ? [
    { id: "weather", icon: Sun, text: `${cond.temp_f}°F · ${cond.conditions}` },
    { id: "wind", icon: Wind, text: `Wind ${cond.wind_mph} mph` },
    { id: "high-tide", icon: Waves, text: `High tide ${fmtClock(hi?.time)}` },
    { id: "low-tide", icon: Waves, text: `Low tide ${fmtClock(lo?.time)}` },
    { id: "sunrise", icon: Sunrise, text: `Sunrise ${fmtClock(cond.sunrise)}` },
    { id: "sunset", icon: Sunset, text: `Sunset ${fmtClock(cond.sunset)}` },
  ] : [];
  return (
    <div data-testid="coastal-conditions" className="mt-5 flex flex-wrap items-center gap-2">
      {!cond && <span className={`h-7 w-64 animate-pulse rounded-full ${chip}`} />}
      {items.map(({ id, icon: Icon, text }) => (
        <span key={id} data-testid={`coastal-${id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold backdrop-blur ${chip}`}>
          <Icon size={13} strokeWidth={2.2} /> {text}
        </span>
      ))}
      <Link to="/events" data-testid="masthead-submit-news-link" className="lift rounded-full bg-[var(--p-amber)] px-4 py-1.5 text-xs font-bold text-[var(--p-ink)] hover:bg-[#ffb52e]">+ Submit your news or event</Link>
    </div>
  );
}

function HeaderSwitcher({ variant, setVariant }) {
  return (
    <div data-testid="header-style-switcher" className="mt-2 flex flex-wrap items-center justify-end gap-1 text-[11px] text-slate-500">
      <span className="mr-1 font-semibold uppercase tracking-wider">Header style</span>
      {Object.entries(VARIANTS).map(([key, val]) => (
        <button key={key} data-testid={`header-style-${key}`} onClick={() => setVariant(key)}
          className={`rounded-full px-2.5 py-0.5 font-semibold transition-colors ${variant === key ? "bg-[var(--p-teal)] text-white" : "hover:bg-slate-200"}`}>
          {val.name}
        </button>
      ))}
    </div>
  );
}

const WAVE = "M0 30 Q180 12 360 30 T720 30 T1080 30 T1440 30 T1800 30 T2160 30 T2520 30 T2880 30";
function WaveLines({ color, ops }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 overflow-hidden ${color}`}>
      {[0, 1, 2].map((i) => (
        <svg key={i} viewBox="0 0 2880 60" preserveAspectRatio="none" className={`coastal-wave coastal-wave-${i} absolute left-0 h-10 w-[200%]`}
          style={{ bottom: `${i * 12 - 4}px`, opacity: ops[i] }}>
          <path d={WAVE} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
      ))}
    </div>
  );
}
